import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../utils/store';
import { Token } from '../types';
import { exportToExcel } from '../utils/excelExport';
import { importFromExcel } from '../utils/excelImport';
import TokenSelection from './TokenSelection';

const TokenTable: React.FC = () => {
  const { 
    tokens, 
    marketingColumns, 
    addToken, 
    editToken,
    removeToken,
    importTokens,
    addMultipleMarketingColumns,
    activeFilter,
    sortColumn,
    sortDirection,
    setSorting,
    counters,
    selectedTokenIds,
    toggleTokenSelection,
    selectAllTokens,
    clearTokenSelection
  } = useStore();
  
  // State for new token form
  const [newToken, setNewToken] = useState<Partial<Token>>({
    TokenName: '',
    TokenWebsiteTraffic: '',
    TokenTotalRaised: '',
    Telegram: '',
    X: '',
    Instagram: '',
  });
  
  // State for marketing entries (arrays of strings)
  const [marketingEntries, setMarketingEntries] = useState<{ [key: string]: string }>({});
  
  // State for editing
  const [editingTokenId, setEditingTokenId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // State for delete confirmation
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);
  
  // State for cell expansion
  const [expandedCell, setExpandedCell] = useState<{tokenId: string, column: string} | null>(null);
  
  // State for compact view
  const [compactView, setCompactView] = useState(false);
  
  // State for active tab in form
  const [activeFormTab, setActiveFormTab] = useState<'basic' | 'social' | 'marketing'>('basic');
  
  // State for auto-growing textareas
  const textareaRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>({});
  
  // State for autocomplete
  const [showSuggestions, setShowSuggestions] = useState<{ [key: string]: boolean }>({});
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<{ [key: string]: number }>({});
  const [filteredSuggestions, setFilteredSuggestions] = useState<{ [key: string]: string[] }>({});
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});
  
  // State for file import
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State for import options
  const [importOptions, setImportOptions] = useState({
    appendData: true,
  });
  
  // State for import modal
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Auto-resize textareas
  useEffect(() => {
    Object.keys(textareaRefs.current).forEach(key => {
      const textarea = textareaRefs.current[key];
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    });
  }, [marketingEntries]);
  
  // Get all existing entries for a marketing column (case-insensitive unique values)
  const getExistingEntries = (column: string): string[] => {
    const entriesSet = new Set<string>();
    
    // Get entries from existing tokens
    tokens.forEach(token => {
      if (Array.isArray(token[column])) {
        (token[column] as string[]).forEach(entry => {
          entriesSet.add(entry);
        });
      }
    });
    
    // Get entries from counters
    if (counters[column]) {
      Object.keys(counters[column]).forEach(entry => {
        entriesSet.add(entry);
      });
    }
    
    return Array.from(entriesSet);
  };
  
  // Handle input change for basic token fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewToken({ ...newToken, [name]: value });
  };
  
  // Handle input change for marketing entries with auto-growing textarea
  const handleMarketingInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>, column: string) => {
    const { value } = e.target;
    setMarketingEntries({ ...marketingEntries, [column]: value });
    
    // Handle autocomplete suggestions
    if (value.trim()) {
      // Get the current word being typed (after the last comma)
      const lastCommaIndex = value.lastIndexOf(',');
      const currentWord = lastCommaIndex >= 0 ? value.slice(lastCommaIndex + 1).trim() : value.trim();
      
      if (currentWord) {
        // Filter existing entries that match the current word (case-insensitive)
        const existingEntries = getExistingEntries(column);
        const suggestions = existingEntries.filter(entry => 
          entry.toLowerCase().includes(currentWord.toLowerCase()) && 
          entry.toLowerCase() !== currentWord.toLowerCase()
        );
        
        setFilteredSuggestions({ ...filteredSuggestions, [column]: suggestions });
        setShowSuggestions({ ...showSuggestions, [column]: true });
        setActiveSuggestionIndex({ ...activeSuggestionIndex, [column]: 0 });
      } else {
        setShowSuggestions({ ...showSuggestions, [column]: false });
      }
    } else {
      setShowSuggestions({ ...showSuggestions, [column]: false });
    }
  };
  
  // Handle selecting a suggestion
  const handleSelectSuggestion = (column: string, suggestion: string) => {
    const currentValue = marketingEntries[column] || '';
    const lastCommaIndex = currentValue.lastIndexOf(',');
    
    // Replace the current word with the selected suggestion
    const newValue = lastCommaIndex >= 0
      ? currentValue.slice(0, lastCommaIndex + 1) + ' ' + suggestion
      : suggestion;
    
    setMarketingEntries({ ...marketingEntries, [column]: newValue });
    setShowSuggestions({ ...showSuggestions, [column]: false });
    
    // Focus the input and place cursor at the end
    if (inputRefs.current[column]) {
      const input = inputRefs.current[column];
      input?.focus();
      setTimeout(() => {
        if (input) {
          input.selectionStart = newValue.length;
          input.selectionEnd = newValue.length;
        }
      }, 0);
    }
  };
  
  // Handle keyboard navigation for suggestions
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, column: string) => {
    const suggestions = filteredSuggestions[column] || [];
    const activeIndex = activeSuggestionIndex[column] || 0;
    
    // If no suggestions or suggestions not shown, return
    if (!showSuggestions[column] || suggestions.length === 0) return;
    
    // Handle arrow down (next suggestion)
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex({ 
        ...activeSuggestionIndex, 
        [column]: (activeIndex + 1) % suggestions.length 
      });
    }
    // Handle arrow up (previous suggestion)
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex({ 
        ...activeSuggestionIndex, 
        [column]: activeIndex === 0 ? suggestions.length - 1 : activeIndex - 1 
      });
    }
    // Handle enter (select suggestion)
    else if (e.key === 'Enter' && showSuggestions[column]) {
      e.preventDefault();
      handleSelectSuggestion(column, suggestions[activeIndex]);
    }
    // Handle escape (close suggestions)
    else if (e.key === 'Escape') {
      setShowSuggestions({ ...showSuggestions, [column]: false });
    }
  };
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      let clickedOutside = true;
      
      // Check if click was inside any of the suggestion boxes or inputs
      Object.keys(showSuggestions).forEach(column => {
        if (inputRefs.current[column] && inputRefs.current[column]?.contains(target)) {
          clickedOutside = false;
        }
        
        const suggestionsList = document.getElementById(`suggestions-${column}`);
        if (suggestionsList && suggestionsList.contains(target)) {
          clickedOutside = false;
        }
      });
      
      if (clickedOutside) {
        // Close all suggestion boxes
        const allClosed = Object.keys(showSuggestions).reduce((acc, column) => {
          acc[column] = false;
          return acc;
        }, {} as { [key: string]: boolean });
        
        setShowSuggestions(allClosed);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);
  
  // Normalize marketing entries to prevent duplicates with different cases
  const normalizeMarketingEntries = (entries: { [key: string]: string }): { [key: string]: string[] } => {
    const normalizedEntries: { [key: string]: string[] } = {};
    
    Object.keys(entries).forEach(column => {
      const entryString = entries[column] || '';
      if (!entryString.trim()) {
        normalizedEntries[column] = [];
        return;
      }
      
      // Split by comma and trim each entry
      const entryArray = entryString.split(',').map(entry => entry.trim()).filter(entry => entry !== '');
      
      // Create a map to track normalized entries (lowercase -> original case)
      const normalizedMap: { [key: string]: string } = {};
      
      // Get existing entries for this column
      const existingEntries = getExistingEntries(column);
      const existingLowercase = existingEntries.map(entry => entry.toLowerCase());
      
      // First pass: check against existing entries
      entryArray.forEach(entry => {
        const lowerEntry = entry.toLowerCase();
        
        // If this entry matches an existing one (case-insensitive), use the existing one
        const existingIndex = existingLowercase.indexOf(lowerEntry);
        if (existingIndex >= 0) {
          normalizedMap[lowerEntry] = existingEntries[existingIndex];
        } else {
          normalizedMap[lowerEntry] = entry;
        }
      });
      
      // Convert back to array with normalized values
      normalizedEntries[column] = Object.values(normalizedMap);
    });
    
    return normalizedEntries;
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Normalize marketing entries to prevent duplicates with different cases
    const marketingData = normalizeMarketingEntries(marketingEntries);
    
    if (isEditing && editingTokenId) {
      // Update existing token
      const tokenData = {
        ...newToken,
        ...marketingData,
        id: editingTokenId,
      } as Token;
      
      editToken(tokenData);
      setIsEditing(false);
      setEditingTokenId(null);
    } else {
      // Create new token
      const tokenData = {
        ...newToken,
        ...marketingData,
      } as Token;
      
      addToken(tokenData);
    }
    
    // Reset form
    setNewToken({
      TokenName: '',
      TokenWebsiteTraffic: '',
      TokenTotalRaised: '',
      Telegram: '',
      X: '',
      Instagram: '',
    });
    setMarketingEntries({});
  };
  
  // Function to start editing a token
  const handleEditClick = (token: Token) => {
    setIsEditing(true);
    setEditingTokenId(token.id);
    
    // Set form values to the token's current values
    setNewToken({
      TokenName: token.TokenName,
      TokenWebsiteTraffic: token.TokenWebsiteTraffic,
      TokenTotalRaised: token.TokenTotalRaised,
      Telegram: token.Telegram || '',
      X: token.X || '',
      Instagram: token.Instagram || '',
    });
    
    // Set marketing entries
    const entries: { [key: string]: string } = {};
    marketingColumns.forEach((column) => {
      if (Array.isArray(token[column])) {
        entries[column] = (token[column] as string[]).join(', ');
      }
    });
    
    setMarketingEntries(entries);
  };
  
  // Function to cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingTokenId(null);
    
    // Reset form
    setNewToken({
      TokenName: '',
      TokenWebsiteTraffic: '',
      TokenTotalRaised: '',
      Telegram: '',
      X: '',
      Instagram: '',
    });
    setMarketingEntries({});
  };
  
  // Function to handle Excel export
  const handleExportToExcel = () => {
    if (tokens.length === 0) {
      alert('No data to export. Please add some tokens first.');
      return;
    }
    
    exportToExcel(tokens, marketingColumns);
  };
  
  // Function to handle column sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if already sorting by this column
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSorting(column, newDirection);
    } else {
      // Default to ascending for new column
      setSorting(column, 'asc');
    }
  };
  
  // Function to render sort indicator
  const renderSortIndicator = (column: string) => {
    if (sortColumn !== column) return null;
    
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };
  
  // Function to check if a row should be highlighted based on active filter
  const shouldHighlight = (token: Token): boolean => {
    if (!activeFilter) return false;
    
    const { column, entry } = activeFilter;
    const marketingEntries = token[column];
    
    if (Array.isArray(marketingEntries)) {
      return marketingEntries.includes(entry);
    }
    
    return false;
  };

  // Render suggestions list
  const renderSuggestions = (column: string) => {
    const suggestions = filteredSuggestions[column] || [];
    const activeIndex = activeSuggestionIndex[column] || 0;
    
    if (!showSuggestions[column] || suggestions.length === 0) return null;
    
    return (
      <ul 
        id={`suggestions-${column}`}
        className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
      >
        {suggestions.map((suggestion, index) => (
          <li
            key={index}
            className={`relative cursor-pointer select-none py-2 pl-3 pr-9 ${
              index === activeIndex ? 'bg-indigo-600 text-white' : 'text-gray-900'
            }`}
            onClick={() => handleSelectSuggestion(column, suggestion)}
          >
            {suggestion}
          </li>
        ))}
      </ul>
    );
  };

  // Function to handle token deletion
  const handleDeleteClick = (tokenId: string) => {
    setDeleteConfirmationId(tokenId);
  };
  
  // Function to confirm token deletion
  const handleConfirmDelete = (tokenId: string) => {
    removeToken(tokenId);
    setDeleteConfirmationId(null);
    
    // If we were editing this token, cancel the edit
    if (editingTokenId === tokenId) {
      handleCancelEdit();
    }
  };
  
  // Function to cancel token deletion
  const handleCancelDelete = () => {
    setDeleteConfirmationId(null);
  };

  // Function to handle cell click for expansion
  const handleCellClick = (tokenId: string, column: string) => {
    if (expandedCell && expandedCell.tokenId === tokenId && expandedCell.column === column) {
      setExpandedCell(null);
    } else {
      setExpandedCell({ tokenId, column });
    }
  };
  
  // Function to render cell content with truncation and expansion
  const renderCellContent = (token: Token, column: string, maxLength: number = 30) => {
    let content: string;
    
    if (Array.isArray(token[column])) {
      content = (token[column] as string[]).join(', ');
    } else if (typeof token[column] === 'string') {
      content = token[column] as string;
    } else {
      content = '';
    }
    
    const isExpanded = expandedCell && expandedCell.tokenId === token.id && expandedCell.column === column;
    const shouldTruncate = content.length > maxLength && !isExpanded;
    
    return (
      <div 
        className={`${shouldTruncate ? 'cursor-pointer' : ''}`}
        onClick={shouldTruncate ? () => handleCellClick(token.id, column) : undefined}
        title={shouldTruncate ? content : undefined}
      >
        {shouldTruncate ? `${content.substring(0, maxLength)}...` : content}
        {isExpanded && (
          <div className="absolute z-10 mt-1 p-2 bg-white shadow-lg border border-gray-200 rounded-md max-w-md">
            <div className="flex justify-between mb-1">
              <span className="font-bold">{column}:</span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedCell(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <div className="text-sm break-words">{content}</div>
          </div>
        )}
      </div>
    );
  };

  // Handle file selection for import
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    setIsImporting(true);
    setImportError(null);
    
    try {
      // Import data from Excel file
      const result = await importFromExcel(file, marketingColumns);
      
      // Add any new marketing columns
      if (result.newMarketingColumns.length > 0) {
        addMultipleMarketingColumns(result.newMarketingColumns);
      }
      
      // Import tokens
      importTokens(result.tokens, importOptions.appendData);
      
      // Close modal and reset
      setShowImportModal(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Show success message
      alert(`Successfully imported ${result.tokens.length} tokens${result.newMarketingColumns.length > 0 ? ` and ${result.newMarketingColumns.length} new marketing columns` : ''}.`);
    } catch (error) {
      console.error('Import error:', error);
      setImportError(error instanceof Error ? error.message : 'Unknown error during import');
    } finally {
      setIsImporting(false);
    }
  };
  
  // Handle import button click
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportError(null);
  };
  
  // Handle import modal close
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Handle import option change
  const handleImportOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setImportOptions({
      ...importOptions,
      [name]: checked,
    });
  };

  // Handle token selection
  const handleTokenSelect = (e: React.MouseEvent, tokenId: string) => {
    // Don't select when clicking on action buttons or checkboxes
    if ((e.target as HTMLElement).closest('button') || 
        (e.target as HTMLElement).closest('input[type="checkbox"]')) {
      return;
    }
    
    toggleTokenSelection(tokenId);
  };
  
  // Handle select all tokens
  const handleSelectAll = () => {
    if (selectedTokenIds.length === tokens.length) {
      clearTokenSelection();
    } else {
      selectAllTokens();
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Edit Token' : 'Add New Token'}
          </h2>
          
          <div className="flex space-x-2">
            <button
              onClick={handleImportClick}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              title="Import data from Excel"
            >
              Import Excel
            </button>
            
            <button
              onClick={() => setCompactView(!compactView)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              title={compactView ? "Switch to normal view" : "Switch to compact view"}
            >
              {compactView ? "Normal View" : "Compact View"}
            </button>
            
            <button
              onClick={handleExportToExcel}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={tokens.length === 0}
              title="Export data to Excel"
            >
              Export Excel
            </button>
          </div>
        </div>
        
        {/* Add/Edit Token Form */}
        <form onSubmit={handleSubmit} className="mt-4 space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
          {/* Form Tabs */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium ${activeFormTab === 'basic' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveFormTab('basic')}
            >
              Basic Info
            </button>
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium ${activeFormTab === 'social' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveFormTab('social')}
            >
              Social Media
            </button>
            <button
              type="button"
              className={`py-2 px-4 text-sm font-medium ${activeFormTab === 'marketing' ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveFormTab('marketing')}
            >
              Marketing ({marketingColumns.length})
            </button>
          </div>
          
          {/* Basic Info Fields */}
          {activeFormTab === 'basic' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="TokenName" className="block text-sm font-medium text-gray-700">
                  Token Name
                </label>
                <input
                  type="text"
                  name="TokenName"
                  id="TokenName"
                  value={newToken.TokenName}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="TokenWebsiteTraffic" className="block text-sm font-medium text-gray-700">
                  Website Traffic
                </label>
                <input
                  type="text"
                  name="TokenWebsiteTraffic"
                  id="TokenWebsiteTraffic"
                  value={newToken.TokenWebsiteTraffic}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="TokenTotalRaised" className="block text-sm font-medium text-gray-700">
                  Total Raised
                </label>
                <input
                  type="text"
                  name="TokenTotalRaised"
                  id="TokenTotalRaised"
                  value={newToken.TokenTotalRaised}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>
          )}
          
          {/* Social Media Fields */}
          {activeFormTab === 'social' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="Telegram" className="block text-sm font-medium text-gray-700">
                  Telegram
                </label>
                <input
                  type="text"
                  name="Telegram"
                  id="Telegram"
                  value={newToken.Telegram}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="e.g. 5000 members"
                />
              </div>
              
              <div>
                <label htmlFor="X" className="block text-sm font-medium text-gray-700">
                  X (Twitter)
                </label>
                <input
                  type="text"
                  name="X"
                  id="X"
                  value={newToken.X}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="e.g. 10000 followers"
                />
              </div>
              
              <div>
                <label htmlFor="Instagram" className="block text-sm font-medium text-gray-700">
                  Instagram
                </label>
                <input
                  type="text"
                  name="Instagram"
                  id="Instagram"
                  value={newToken.Instagram}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="e.g. 8000 followers"
                />
              </div>
            </div>
          )}
          
          {/* Marketing Columns Inputs */}
          {activeFormTab === 'marketing' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {marketingColumns.map((column) => (
                <div key={column} className="relative">
                  <label htmlFor={column} className="block text-sm font-medium text-gray-700">
                    {column} (comma-separated)
                  </label>
                  <textarea
                    name={column}
                    id={column}
                    ref={el => {
                      textareaRefs.current[column] = el;
                      if (el) inputRefs.current[column] = el;
                    }}
                    value={marketingEntries[column] || ''}
                    onChange={(e) => handleMarketingInputChange(e, column)}
                    onKeyDown={(e) => handleKeyDown(e, column)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm resize-none overflow-hidden"
                    placeholder="e.g. Tronweekly, Cryptoupdates"
                    autoComplete="off"
                    rows={1}
                  />
                  {renderSuggestions(column)}
                </div>
              ))}
            </div>
          )}
          
          <div className="flex space-x-3 pt-3 border-t border-gray-200">
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {isEditing ? 'Save Changes' : 'Add Token'}
            </button>
            
            {isEditing && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
        
        {/* Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseImportModal}></div>
              
              {/* Modal panel */}
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Import from Excel
                      </h3>
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-4">
                          Select an Excel file to import. The file should have columns matching the application's structure.
                        </p>
                        
                        {/* Import options */}
                        <div className="mb-4">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              name="appendData"
                              checked={importOptions.appendData}
                              onChange={handleImportOptionChange}
                              className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">Append to existing data (uncheck to replace all data)</span>
                          </label>
                        </div>
                        
                        {/* File input */}
                        <div className="mt-2">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept=".xlsx,.xls"
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                            disabled={isImporting}
                          />
                        </div>
                        
                        {/* Expected format info */}
                        <div className="mt-4 text-xs text-gray-500">
                          <p className="font-semibold mb-1">Expected column format:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>Token Name</li>
                            <li>Website Traffic</li>
                            <li>Total Raised</li>
                            <li>Telegram</li>
                            <li>X (Twitter)</li>
                            <li>Instagram</li>
                            <li>Any additional columns will be treated as marketing columns</li>
                          </ul>
                        </div>
                        
                        {/* Error message */}
                        {importError && (
                          <div className="mt-4 p-2 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{importError}</p>
                          </div>
                        )}
                        
                        {/* Loading indicator */}
                        {isImporting && (
                          <div className="mt-4 flex justify-center">
                            <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="ml-2 text-sm text-gray-700">Importing...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleCloseImportModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Selection info */}
        {selectedTokenIds.length > 0 && (
          <div className="mt-4 mb-2 bg-blue-50 p-2 rounded-md border border-blue-200 flex justify-between items-center">
            <span className="text-sm text-blue-700">
              {selectedTokenIds.length} token{selectedTokenIds.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={clearTokenSelection}
              className="text-xs text-blue-600 hover:text-blue-800 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded-full"
            >
              Clear Selection
            </button>
          </div>
        )}
        
        {/* Tokens Table */}
        <div className="mt-6 overflow-x-auto" style={{ maxWidth: '100%' }}>
          <table className={`min-w-full divide-y divide-gray-200 table-fixed ${compactView ? 'text-xs' : ''}`}>
            <thead className="bg-gray-50">
              <tr>
                <th 
                  scope="col" 
                  className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                  onClick={handleSelectAll}
                  style={{ width: '40px' }}
                >
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      checked={selectedTokenIds.length > 0 && selectedTokenIds.length === tokens.length}
                      onChange={handleSelectAll}
                      onClick={(e) => e.stopPropagation()}
                      title={selectedTokenIds.length === tokens.length ? "Deselect all tokens" : "Select all tokens"}
                    />
                  </div>
                </th>
                <th 
                  scope="col" 
                  className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                  onClick={() => handleSort('TokenName')}
                  style={{ width: '15%' }}
                >
                  Token Name {renderSortIndicator('TokenName')}
                </th>
                <th 
                  scope="col" 
                  className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                  onClick={() => handleSort('TokenWebsiteTraffic')}
                  style={{ width: '10%' }}
                >
                  Traffic {renderSortIndicator('TokenWebsiteTraffic')}
                </th>
                <th 
                  scope="col" 
                  className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                  onClick={() => handleSort('TokenTotalRaised')}
                  style={{ width: '10%' }}
                >
                  Raised {renderSortIndicator('TokenTotalRaised')}
                </th>
                <th 
                  scope="col" 
                  className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                  onClick={() => handleSort('Telegram')}
                  style={{ width: '8%' }}
                >
                  TG {renderSortIndicator('Telegram')}
                </th>
                <th 
                  scope="col" 
                  className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                  onClick={() => handleSort('X')}
                  style={{ width: '8%' }}
                >
                  X {renderSortIndicator('X')}
                </th>
                <th 
                  scope="col" 
                  className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                  onClick={() => handleSort('Instagram')}
                  style={{ width: '8%' }}
                >
                  IG {renderSortIndicator('Instagram')}
                </th>
                {marketingColumns.map((column) => (
                  <th 
                    key={column} 
                    scope="col" 
                    className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                    onClick={() => handleSort(column)}
                    style={{ width: `${Math.max(8, Math.min(15, 100 / (marketingColumns.length + 7)))}%` }}
                  >
                    {column.length > 10 ? column.substring(0, 10) + '...' : column} {renderSortIndicator(column)}
                  </th>
                ))}
                <th scope="col" className={`px-3 ${compactView ? 'py-1' : 'py-3'} text-right text-xs font-medium text-gray-500 uppercase tracking-wider`} style={{ width: '10%' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tokens.map((token) => (
                <tr 
                  key={token.id} 
                  className={`${shouldHighlight(token) ? 'bg-yellow-50' : ''} ${selectedTokenIds.includes(token.id) ? 'bg-blue-50' : ''} hover:bg-gray-50 cursor-pointer`}
                  onClick={(e) => handleTokenSelect(e, token.id)}
                >
                  <TokenSelection token={token} isCompact={compactView} />
                  <td className={`px-3 ${compactView ? 'py-1' : 'py-3'} whitespace-nowrap text-sm font-medium text-gray-900 relative`}>
                    {renderCellContent(token, 'TokenName')}
                  </td>
                  <td className={`px-3 ${compactView ? 'py-1' : 'py-3'} whitespace-nowrap text-sm text-gray-500 relative`}>
                    {renderCellContent(token, 'TokenWebsiteTraffic')}
                  </td>
                  <td className={`px-3 ${compactView ? 'py-1' : 'py-3'} whitespace-nowrap text-sm text-gray-500 relative`}>
                    {renderCellContent(token, 'TokenTotalRaised')}
                  </td>
                  <td className={`px-3 ${compactView ? 'py-1' : 'py-3'} whitespace-nowrap text-sm text-gray-500 relative`}>
                    {renderCellContent(token, 'Telegram')}
                  </td>
                  <td className={`px-3 ${compactView ? 'py-1' : 'py-3'} whitespace-nowrap text-sm text-gray-500 relative`}>
                    {renderCellContent(token, 'X')}
                  </td>
                  <td className={`px-3 ${compactView ? 'py-1' : 'py-3'} whitespace-nowrap text-sm text-gray-500 relative`}>
                    {renderCellContent(token, 'Instagram')}
                  </td>
                  {marketingColumns.map((column) => (
                    <td key={`${token.id}-${column}`} className={`px-3 ${compactView ? 'py-1' : 'py-3'} whitespace-nowrap text-sm text-gray-500 relative`}>
                      {renderCellContent(token, column)}
                    </td>
                  ))}
                  <td className={`px-3 ${compactView ? 'py-1' : 'py-3'} whitespace-nowrap text-right text-sm font-medium`}>
                    {deleteConfirmationId === token.id ? (
                      <div className="flex justify-end space-x-2">
                        <span className="text-red-600 mr-2">Confirm?</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConfirmDelete(token.id);
                          }}
                          className="text-red-600 hover:text-red-900 font-bold"
                          disabled={isEditing}
                        >
                          Y
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelDelete();
                          }}
                          className="text-gray-600 hover:text-gray-900"
                          disabled={isEditing}
                        >
                          N
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(token);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          disabled={isEditing || deleteConfirmationId !== null}
                        >
                          {compactView ? 'E' : 'Edit'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(token.id);
                          }}
                          className="text-red-600 hover:text-red-900"
                          disabled={isEditing || deleteConfirmationId !== null}
                        >
                          {compactView ? 'D' : 'Del'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TokenTable; 